import { useTranslation } from 'react-i18next';
import { ISelectOption, Select, Table } from '@salecycle/ui-component-library';
import styled from 'styled-components';
import { useImportConsentContext } from '../context/ImportConsentContext';
import { Amber, ColumnType, Column } from '../types';

const SelectBoxes = styled.div`
  display: flex;
`;

const SelectBox = styled.div`
  flex: 1;
  margin-right: 30px;
`;

const DataArea = styled.div`
  flex: 1;
  margin-right: 30px;
`;

export function SelectBoxArea() {
  const { t } = useTranslation('fileManagement');
  const { analysedFileData, setEmailHeadingSelected, setDateHeadingSelected } = useImportConsentContext();

  function selectOptions(data: Column[]): ISelectOption<string>[] {
    return data.map(datum => ({
      label: datum.heading.replace('-', '. ').replace(/-/g, ' '), // prettier numbered bullets list for users
      value: datum.heading,
    }));
  }

  return (
    <DataArea>
      <SelectBoxes>
        <SelectBox data-testid="email-select">
          <Select
            ariaLabel={t('analysis.file.analysed.amber.modal.selectors.emailFieldName')}
            label={t('analysis.file.analysed.amber.modal.selectors.emailFieldName')}
            hint="unable to see hint in designs"
            searchable={false}
            options={selectOptions(
              (analysedFileData.status as Amber).columns.filter(col => col.type === ColumnType.EMAIL),
            )}
            onChange={selected => setEmailHeadingSelected(selected.value)}
          />
        </SelectBox>
        <SelectBox data-testid="date-select">
          <Select
            ariaLabel={t('analysis.file.analysed.amber.modal.selectors.effectiveDateFieldName')}
            label={t('analysis.file.analysed.amber.modal.selectors.effectiveDateFieldName')}
            hint="unable to see hint in designs"
            searchable={false}
            options={[
              { label: 'today', value: '' },
              ...selectOptions((analysedFileData.status as Amber).columns.filter(col => col.type === ColumnType.DATE)),
            ]}
            onChange={selected => setDateHeadingSelected(selected.value)}
          />
        </SelectBox>
      </SelectBoxes>
    </DataArea>
  );
}

export function PreviewTableArea() {
  const { t } = useTranslation('fileManagement');
  const previewTableRowLength = 5;
  const tableProps = {
    sortable: false,
    pageSize: previewTableRowLength,
    showPagination: false,
    columns: [
      {
        Header: t('analysis.file.analysed.amber.previewTable.header.email'),
        accessor: 'email',
      },
      {
        Header: t('analysis.file.analysed.amber.previewTable.header.date'),
        accessor: 'date',
      },
    ],
    data: new Array(previewTableRowLength).fill({
      email: '-',
      date: t('analysis.file.analysed.amber.previewTable.date.default'),
    }),
  };

  const { analysedFileData, emailHeadingSelected, dateHeadingSelected } = useImportConsentContext();

  if (emailHeadingSelected) {
    const emailData = (analysedFileData.status as Amber).columns.find(col => {
      return col.heading === emailHeadingSelected;
    }).rows;
    tableProps.data = tableProps.data.map(({ date }, index) => ({ email: emailData[index], date }));
  }

  const dateData =
    (analysedFileData.status as Amber).columns.find(col => {
      return col.heading === dateHeadingSelected;
    })?.rows ?? [];
  tableProps.data = tableProps.data.map(({ email }, index) => ({
    email,
    date: dateData[index] ?? t('analysis.file.analysed.amber.previewTable.date.default'),
  }));

  return <Table {...tableProps} />;
}
